import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select,
  TextField, Typography, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { motion, useReducedMotion } from 'framer-motion';
import { GuestGateMessage } from './GuestGateMessage';
import {
  DEFAULT_FAMILY_TREE_SETTINGS, deleteFamilyMember, saveFamilyMember,
  loadFamilyMemberPhoto, saveFamilyTreeSettings, subscribeFamilyTree,
} from '../../firebase/familyTreeService';
import { getGenerationRows, getRelationshipConnections, memberLabel } from './familyTreeLayout';
import { designTokens } from '../../theme/theme';

const EMPTY_MEMBER = {
  firstName: '', surname: '', birthDate: '', deathDate: '', countryOfBirth: '',
  profession: '', facts: '', parentIds: [], spouseIds: [], photo: null,
};

const FONT_SIZES = { small: 12, medium: 14, large: 17 };
const TREE_CARD_WIDTH = 176;
const TREE_CARD_HEIGHT = 190;
const TREE_COLUMN_GAP = 82;
const TREE_ROW_HEIGHT = 232;
const TREE_PADDING = 28;
const TREE_COLORS = {
  classic: { accent: designTokens.colors.success, border: `color-mix(in srgb, ${designTokens.colors.success} 36%, transparent)`, background: 'var(--app-surface)', text: 'var(--app-text-primary)', muted: 'var(--app-text-secondary)' },
  modern: { accent: designTokens.colors.primary, border: `color-mix(in srgb, ${designTokens.colors.primary} 36%, transparent)`, background: 'var(--app-surface)', text: 'var(--app-text-primary)', muted: 'var(--app-text-secondary)' },
  compact: { accent: designTokens.colors.warning, border: `color-mix(in srgb, ${designTokens.colors.warning} 36%, transparent)`, background: 'var(--app-surface)', text: 'var(--app-text-primary)', muted: 'var(--app-text-secondary)' },
};

function useFamilyMemberPhoto(userId, photo) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const photoPath = photo?.path;
  const contentType = photo?.contentType;

  useEffect(() => {
    if (!photoPath) {
      setUrl('');
      setFailed(false);
      return undefined;
    }
    let active = true;
    let objectUrl = '';
    setUrl('');
    setFailed(false);
    loadFamilyMemberPhoto(userId, { path: photoPath, contentType }).then((loadedUrl) => {
      objectUrl = loadedUrl;
      if (active) setUrl(loadedUrl);
      else URL.revokeObjectURL(loadedUrl);
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, photoPath, contentType]);

  return { url, failed };
}

function formatDate(value, dateFormat) {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  if (dateFormat === 'MM/DD/YYYY') return `${month}/${day}/${year}`;
  if (dateFormat === 'YYYY-MM-DD') return value;
  return `${day}/${month}/${year}`;
}

function escapeXml(value) {
  return String(value || '').replace(/[<>&'"]/g, (character) => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;',
  }[character]));
}

function MemberDialog({ open, member, members, defaultSurname, userId, onClose, onSave }) {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [photoFile, setPhotoFile] = useState(null);
  const [originalPhoto, setOriginalPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savedPhoto = useFamilyMemberPhoto(userId, form.photo);

  useEffect(() => {
    if (!open) return undefined;
    setForm(member ? { ...EMPTY_MEMBER, ...member } : { ...EMPTY_MEMBER, surname: defaultSurname });
    setPhotoFile(null);
    setOriginalPhoto(member?.photo || null);
    setPhotoPreview('');
    setError('');
    setSaving(false);
    return undefined;
  }, [open, member, defaultSurname]);

  useEffect(() => {
    if (!photoFile) return undefined;
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const descendants = useMemo(() => {
    if (!member) return new Set();
    const found = new Set([member.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const candidate of members) {
        if (!found.has(candidate.id) && (candidate.parentIds || []).some((id) => found.has(id))) {
          found.add(candidate.id);
          changed = true;
        }
      }
    }
    return found;
  }, [member, members]);

  const setField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!form.firstName.trim()) {
      setError('Enter a first name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form, photoFile, originalPhoto);
      onClose();
    } catch (saveError) {
      setError(saveError.message || 'Could not save this family member.');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={isMobile} fullWidth maxWidth="sm"
      PaperProps={{ sx: {
        bgcolor: 'var(--app-surface)', color: 'var(--app-text-primary)', border: '1px solid var(--app-border-hover)',
        borderRadius: isMobile ? 0 : 3,
        display: 'flex', flexDirection: 'column', height: isMobile ? '100dvh' : undefined,
      } }}>
      <DialogTitle sx={{
        px: { xs: 2, sm: 3 }, py: 2,
        background: 'linear-gradient(135deg, rgba(6,214,160,0.12), rgba(76,201,240,0.06))',
        borderBottom: '1px solid var(--app-border-subtle)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{
            width: 44, height: 44, bgcolor: 'rgba(6,214,160,0.16)',
            color: 'var(--app-success)', border: '1px solid rgba(6,214,160,0.28)',
          }}>
            {form.firstName ? form.firstName.slice(0, 1).toUpperCase() : <AddIcon />}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.12rem' }}>
              {member ? 'Edit family member' : 'Add family member'}
            </Typography>
            <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.76rem', mt: 0.25 }}>
              Add their details and connect them to your family.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{
        display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 2, sm: 3 },
        pt: '20px !important', overscrollBehavior: 'contain', flex: 1, minHeight: 0,
      }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{
          p: { xs: 1.5, sm: 2 }, borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid var(--app-border-subtle)',
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--app-success)', fontWeight: 900, letterSpacing: '0.1em', mb: 1.4 }}>
            PERSONAL DETAILS
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <TextField label="First name" value={form.firstName} onChange={setField('firstName')} required inputProps={{ maxLength: 80 }} />
            <TextField label="Surname" value={form.surname} onChange={setField('surname')} inputProps={{ maxLength: 80 }} />
            <TextField label="Date of birth" type="date" value={form.birthDate} onChange={setField('birthDate')}
              InputLabelProps={{ shrink: true }} />
            <TextField label="Date of death" type="date" value={form.deathDate} onChange={setField('deathDate')}
              InputLabelProps={{ shrink: true }} />
            <TextField label="Country of birth" value={form.countryOfBirth} onChange={setField('countryOfBirth')} inputProps={{ maxLength: 100 }} />
            <TextField label="Profession" value={form.profession} onChange={setField('profession')} inputProps={{ maxLength: 100 }} />
          </Box>
        </Box>
        <Box sx={{
          p: { xs: 1.5, sm: 2 }, borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid var(--app-border-subtle)',
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--app-primary)', fontWeight: 900, letterSpacing: '0.1em', mb: 1.4 }}>
            FAMILY CONNECTIONS
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl>
              <InputLabel id="family-parents-label">Parents</InputLabel>
              <Select labelId="family-parents-label" multiple label="Parents" value={form.parentIds || []}
                onChange={setField('parentIds')}
                renderValue={(ids) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {ids.map((id) => (
                      <Chip key={id} size="small" label={memberLabel(members.find((item) => item.id === id) || {})}
                        sx={{ bgcolor: 'rgba(6,214,160,0.12)', color: '#b8f5df', maxWidth: '100%' }} />
                    ))}
                  </Box>
                )}>
                {members.filter((item) => !descendants.has(item.id)).map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox checked={(form.parentIds || []).includes(item.id)} />
                    {memberLabel(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="family-partners-label">Spouse or partner</InputLabel>
              <Select labelId="family-partners-label" multiple label="Spouse or partner" value={form.spouseIds || []}
                onChange={setField('spouseIds')}
                renderValue={(ids) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {ids.map((id) => (
                      <Chip key={id} size="small" label={memberLabel(members.find((item) => item.id === id) || {})}
                        sx={{ bgcolor: 'rgba(247,37,133,0.12)', color: '#ffb2d1', maxWidth: '100%' }} />
                    ))}
                  </Box>
                )}>
                {members.filter((item) => item.id !== member?.id).map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox checked={(form.spouseIds || []).includes(item.id)} />
                    {memberLabel(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{
          p: { xs: 1.5, sm: 2 }, borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid var(--app-border-subtle)',
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'var(--app-warning)', fontWeight: 900, letterSpacing: '0.1em', mb: 1.4 }}>
            STORY & PHOTO
          </Typography>
          <TextField fullWidth label="Interesting facts or life story" value={form.facts} onChange={setField('facts')}
            multiline minRows={3} inputProps={{ maxLength: 2000 }} />
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.2, mt: 1.5, p: 1.2,
            borderRadius: 2, border: '1px dashed rgba(255,255,255,0.18)',
            bgcolor: 'rgba(255,255,255,0.02)',
          }}>
            {(photoPreview || savedPhoto.url) && (
              <Avatar src={photoPreview || savedPhoto.url} alt="" sx={{ width: 48, height: 48, flexShrink: 0 }} />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Button component="label" size="small" variant="outlined" startIcon={<PhotoCameraIcon />}
                sx={{ minHeight: 40 }}>
                {form.photo || photoFile ? 'Change photo' : 'Choose photo'}
                <input hidden type="file" accept="image/*" onChange={(event) => {
                  setPhotoFile(event.target.files?.[0] || null);
                  event.target.value = '';
                }} />
              </Button>
              <Typography noWrap sx={{ color: 'var(--app-text-secondary)', fontSize: '0.7rem', mt: 0.5 }}>
                {photoFile?.name || (form.photo ? 'Saved photo · up to 5 MB' : 'Optional · JPG, PNG or WebP')}
              </Typography>
            </Box>
            {form.photo && (
              <IconButton aria-label="Remove photo" onClick={() => {
                setForm((current) => ({ ...current, photo: null }));
                setPhotoFile(null);
              }} sx={{ color: '#f87171', minWidth: 40, minHeight: 40 }}>
                <DeleteOutlineIcon />
              </IconButton>
            )}
            {savedPhoto.failed && (
              <Typography role="alert" sx={{ color: '#fca5a5', fontSize: '0.7rem' }}>Saved photo could not be loaded.</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{
        px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 },
        pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 2 },
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        flexShrink: 0,
        borderTop: '1px solid var(--app-border-subtle)',
        bgcolor: 'var(--app-surface)',
      }}>
        <Button onClick={onClose} disabled={saving} fullWidth={isMobile} sx={{ color: '#9aa4af', minHeight: 44 }}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} fullWidth={isMobile} variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ minHeight: 44, bgcolor: 'var(--app-success)', color: '#08130f', fontWeight: 800, '&:hover': { bgcolor: '#05bd8d' } }}>
          {saving ? 'Saving' : 'Save member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MemberCard({ userId, member, members, settings, onEdit, onDelete }) {
  const theme = TREE_COLORS[settings.treeStyle] || TREE_COLORS.classic;
  const textSize = FONT_SIZES[settings.fontSize] || FONT_SIZES.medium;
  const photo = useFamilyMemberPhoto(userId, member.photo);
  const partners = members.filter((item) =>
    (member.spouseIds || []).includes(item.id) || (item.spouseIds || []).includes(member.id));
  const dates = [formatDate(member.birthDate, settings.dateFormat), formatDate(member.deathDate, settings.dateFormat)]
    .filter(Boolean).join(' – ');

  return (
    <Box sx={{
      width: TREE_CARD_WIDTH, height: TREE_CARD_HEIGHT, boxSizing: 'border-box',
      position: 'relative', p: 1.2, borderRadius: settings.treeStyle === 'classic' ? 2 : 3, overflow: 'hidden',
      background: theme.background, border: `1px solid ${theme.border}`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.32)',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
      '&:hover': {
        borderColor: theme.accent,
        boxShadow: `0 10px 28px ${theme.accent}22`,
        transform: 'translateY(-2px)',
      },
    }}>
      <Box sx={{ position: 'absolute', top: 3, right: 3, display: 'flex' }}>
        <IconButton aria-label={`Edit ${memberLabel(member)}`} onClick={() => onEdit(member)} size="small"
          sx={{ width: 25, height: 25, color: theme.muted }}>
          <EditIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton aria-label={`Delete ${memberLabel(member)}`} onClick={() => onDelete(member)} size="small"
          sx={{ width: 25, height: 25, color: '#b66b60' }}>
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: 0.5 }}>
        <Avatar src={photo.url || undefined} alt={memberLabel(member)}
          sx={{ width: 54, height: 54, mb: 0.7, bgcolor: `${theme.accent}20`, color: theme.accent, fontWeight: 900 }}>
          {member.firstName?.slice(0, 1)?.toUpperCase()}
        </Avatar>
        <Typography sx={{
          color: theme.text, width: '100%', fontSize: textSize, fontWeight: 900,
          lineHeight: 1.25, overflowWrap: 'anywhere',
          display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden',
        }}>
          {memberLabel(member)}
        </Typography>
        {dates && <Typography sx={{ mt: 0.4, color: theme.muted, fontSize: textSize - 2 }}>{dates}</Typography>}
      </Box>
      {photo.failed && (
        <Typography role="alert" sx={{ mt: 0.6, color: '#fca5a5', fontSize: '0.68rem' }}>
          Could not load this member’s photo.
        </Typography>
      )}
      {(member.profession || member.countryOfBirth) && (
        <Typography sx={{
          mt: 0.6, color: theme.text, fontSize: textSize - 1, textAlign: 'center',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {[member.profession, member.countryOfBirth].filter(Boolean).join(' · ')}
        </Typography>
      )}
      {partners.length > 0 && (
        <Typography sx={{ mt: 0.6, color: theme.muted, fontSize: textSize - 2, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {partners.map(memberLabel).join(' · ')}
        </Typography>
      )}
      {member.facts && (
        <Typography sx={{
          mt: 0.6, color: theme.muted, fontSize: textSize - 2,
          whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
          display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden',
        }}>
          {member.facts}
        </Typography>
      )}
    </Box>
  );
}

export function FamilyTreeSection({ userId, isAnonymous }) {
  const reduceMotion = useReducedMotion();
  const [settings, setSettings] = useState(DEFAULT_FAMILY_TREE_SETTINGS);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [dialogMember, setDialogMember] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [zoom, setZoom] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const treeViewportRef = useRef(null);

  useEffect(() => {
    if (!userId || isAnonymous) return undefined;
    setLoading(true);
    setError('');
    return subscribeFamilyTree(userId, {
      onSettings: (nextSettings) => {
        setSettings(nextSettings);
        setLoading(false);
      },
      onMembers: (nextMembers) => {
        setMembers(nextMembers);
        setLoading(false);
      },
      onError: (loadError) => {
        setError(`Could not load your family tree: ${loadError.message || 'Firebase request failed.'}`);
        setLoading(false);
      },
    });
  }, [userId, isAnonymous]);

  const updateSettings = (key) => (event) => {
    setSettings((current) => ({ ...current, [key]: event.target.value }));
    setNotice('');
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError('');
    setNotice('');
    try {
      await saveFamilyTreeSettings(userId, settings);
      setNotice('Tree settings saved.');
    } catch (saveError) {
      setError(`Could not save tree settings: ${saveError.message || 'Firebase request failed.'}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveMember = async (form, photoFile, originalPhoto) => {
    setError('');
    setNotice('');
    const result = await saveFamilyMember(userId, form, photoFile, originalPhoto);
    setNotice(result.photoCleanupFailed
      ? 'Member saved, but an older photo could not be removed from storage.'
      : 'Family member saved.');
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Delete ${memberLabel(member)} from your family tree?`)) return;
    setError('');
    setNotice('');
    try {
      const result = await deleteFamilyMember(userId, member);
      setNotice(result.photoCleanupFailed
        ? 'Member deleted, but their photo could not be removed from storage.'
        : 'Family member deleted.');
    } catch (deleteError) {
      setError(`Could not delete this family member: ${deleteError.message || 'Firebase request failed.'}`);
    }
  };

  const rows = useMemo(() => getGenerationRows(members), [members]);
  const memberPositions = new Map();
  const maxTreeX = Math.max(0, ...rows.flatMap((row) => row.members.map((member) => member.treeX)));
  rows.forEach((row, rowIndex) => {
    row.members.forEach((member) => {
      memberPositions.set(member.id, {
        x: TREE_PADDING + member.treeX * (TREE_CARD_WIDTH + TREE_COLUMN_GAP) + TREE_CARD_WIDTH / 2,
        top: TREE_PADDING + rowIndex * TREE_ROW_HEIGHT + 34,
        rowIndex,
      });
    });
  });
  const treeWidth = TREE_PADDING * 2 + maxTreeX * (TREE_CARD_WIDTH + TREE_COLUMN_GAP) + TREE_CARD_WIDTH;
  const treeHeight = TREE_PADDING * 2 + Math.max(1, rows.length) * TREE_ROW_HEIGHT;
  const treeTheme = TREE_COLORS[settings.treeStyle] || TREE_COLORS.classic;
  const relationshipColors = {
    parent: treeTheme.accent,
    spouse: designTokens.colors.secondary,
    sibling: '#B388FF',
  };
  const matchingMemberId = searchTerm.trim()
    ? members.find((member) => memberLabel(member).toLowerCase().includes(searchTerm.trim().toLowerCase()))?.id
    : null;

  const exportTree = () => {
    const connections = getRelationshipConnections(members, memberPositions, TREE_CARD_WIDTH, TREE_CARD_HEIGHT)
      .map(({ type, d }) => `<path d="${d}" fill="none" stroke="${relationshipColors[type]}" stroke-width="${type === 'parent' ? 2 : 3}"${type === 'spouse' ? ' stroke-linecap="round"' : ''}/>`).join('');
    const nodes = members.map((member) => {
      const position = memberPositions.get(member.id);
      if (!position) return '';
      const name = escapeXml(memberLabel(member));
      const dates = escapeXml([formatDate(member.birthDate, settings.dateFormat), formatDate(member.deathDate, settings.dateFormat)].filter(Boolean).join(' – '));
      const profession = escapeXml(member.profession || member.countryOfBirth);
      return `<g><rect x="${position.x - TREE_CARD_WIDTH / 2}" y="${position.top}" width="${TREE_CARD_WIDTH}" height="${TREE_CARD_HEIGHT}" rx="14" fill="${treeTheme.background}" stroke="${treeTheme.border}"/><circle cx="${position.x}" cy="${position.top + 42}" r="25" fill="${treeTheme.accent}" opacity=".18"/><text x="${position.x}" y="${position.top + 47}" text-anchor="middle" font-family="sans-serif" font-size="16" font-weight="700" fill="${treeTheme.accent}">${escapeXml(member.firstName.slice(0, 1).toUpperCase())}</text><text x="${position.x}" y="${position.top + 88}" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="700" fill="${treeTheme.text}">${name}</text><text x="${position.x}" y="${position.top + 108}" text-anchor="middle" font-family="sans-serif" font-size="11" fill="${treeTheme.muted}">${dates}</text><text x="${position.x}" y="${position.top + 130}" text-anchor="middle" font-family="sans-serif" font-size="11" fill="${treeTheme.text}">${profession}</text></g>`;
    }).join('');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${treeWidth}" height="${treeHeight}" viewBox="0 0 ${treeWidth} ${treeHeight}"><rect width="100%" height="100%" fill="#0d1117"/>${connections}${nodes}</svg>`;
    const downloadUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${(settings.surname || 'family-tree').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-tree.svg`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
  };

  if (isAnonymous) return <GuestGateMessage feature="Family Tree" />;

  return (
    <Box sx={{ color: 'var(--app-text-primary)', pb: { xs: 1, sm: 2 } }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 0.5,
        '& .family-tree-title-icon': {
          p: 0.7, borderRadius: 2.5, color: 'var(--app-success)',
          bgcolor: 'rgba(6,214,160,0.12)', border: '1px solid rgba(6,214,160,0.24)',
          boxSizing: 'content-box',
        },
      }}>
        <AccountTreeIcon className="family-tree-title-icon" />
        <Typography sx={{
          fontSize: '1.2rem', fontWeight: 900,
          background: 'linear-gradient(110deg, var(--app-text-primary) 15%, var(--app-success) 90%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>Family Tree</Typography>
      </Box>
      <Typography sx={{ color: '#9aa4af', fontSize: '0.82rem', lineHeight: 1.6, mb: { xs: 1, sm: 1.5 } }}>
        Add relatives, connect parents and partners, and preserve the details and stories that matter.
      </Typography>
      <Alert severity="info" sx={{
        mb: { xs: 1, sm: 1.5 }, py: { xs: 0.1, sm: 0.5 },
        bgcolor: 'rgba(76,201,240,0.08)', color: 'var(--app-text-soft)',
        border: '1px solid rgba(76,201,240,0.12)', borderRadius: 2.5,
        '& .MuiAlert-icon': { color: 'var(--app-primary)' },
      }}>
        Your tree and photos are private to your Google account. Only add personal details and photos you have permission to store.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1.5 }}>{notice}</Alert>}

      <Box sx={{
        display: 'grid', gap: { xs: 1, sm: 1.5 }, alignItems: 'start',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '300px minmax(0, 1fr)' },
        gridTemplateAreas: { xs: '"panel" "canvas"', lg: '"panel canvas"' },
      }}>
        <Box sx={{
          gridArea: 'panel', p: { xs: 1.1, sm: 1.5 }, borderRadius: 3,
          background: 'linear-gradient(145deg, rgba(22,27,34,0.98), rgba(17,24,32,0.96))',
          border: '1px solid rgba(255,255,255,0.09)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        }}>
          <Typography sx={{ color: 'var(--app-text-primary)', fontSize: '1rem', fontWeight: 900 }}>Your family</Typography>
          <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.72rem', lineHeight: 1.45, mt: 0.2, mb: 0.7 }}>
            Add relatives, then connect parents and partners.
          </Typography>
          <Button fullWidth onClick={() => { setDialogMember(null); setDialogOpen(true); }}
            startIcon={<AddIcon />} variant="contained"
            sx={{
              mb: 1, minHeight: 40, borderRadius: 2,
              background: 'linear-gradient(120deg, var(--app-success), #00b887)',
              color: 'var(--app-background)', fontWeight: 900,
              boxShadow: '0 6px 20px rgba(6,214,160,0.16)',
              '&:hover': { background: 'linear-gradient(120deg, #18e5ae, #06c897)' },
            }}>
            Add family member
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.45 }}>
            <Typography sx={{ color: '#9aa4af', fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.08em' }}>
              PEOPLE
            </Typography>
            <Chip size="small" label={members.length} sx={{
              height: 22, minWidth: 28, bgcolor: 'rgba(6,214,160,0.1)',
              color: 'var(--app-success)', fontWeight: 900, fontSize: '0.68rem',
            }} />
          </Box>
          <Box sx={{
            display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 0.45, maxHeight: { xs: 160, sm: 190 }, overflowY: 'auto', mb: 1.1,
            pr: 0.25, overscrollBehavior: 'contain', alignContent: 'start',
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.14)', borderRadius: 4 },
          }}>
            {members.map((member, index) => (
              <Button key={member.id} fullWidth onClick={() => { setDialogMember(member); setDialogOpen(true); }}
                aria-label={`Edit ${memberLabel(member)}`}
                sx={{
                  minWidth: 0, minHeight: 36, justifyContent: 'flex-start',
                  px: 0.65, py: 0.35, gap: 0.65,
                  color: 'var(--app-text-soft)', textTransform: 'none', fontWeight: 700,
                  fontSize: '0.72rem', borderRadius: 1.5,
                  bgcolor: index % 2 === 0 ? 'rgba(255,255,255,0.025)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(6,214,160,0.1)' },
                }}>
                <Avatar sx={{
                  width: 25, height: 25, flexShrink: 0, bgcolor: 'rgba(6,214,160,0.16)',
                  color: 'var(--app-success)', fontSize: '0.66rem', fontWeight: 900,
                }}>
                  {member.firstName?.slice(0, 1)?.toUpperCase()}
                </Avatar>
                <Box component="span" sx={{
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', textAlign: 'left',
                }}>
                  {memberLabel(member)}
                </Box>
              </Button>
            ))}
            {!members.length && !loading && (
              <Typography sx={{ color: '#737f8c', fontSize: '0.74rem', py: 1 }}>
                Add your first person to begin.
              </Typography>
            )}
          </Box>
          <Box sx={{ pt: 1, borderTop: '1px solid var(--app-border-subtle)' }}>
            <Typography sx={{ color: '#9aa4af', fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.08em', mb: 0.7 }}>
              TREE SETTINGS
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.8 }}>
              <TextField sx={{ gridColumn: '1 / -1' }} label="Family surname" value={settings.surname} onChange={updateSettings('surname')} />
              <FormControl>
                <InputLabel id="tree-style-label">Tree style</InputLabel>
                <Select labelId="tree-style-label" label="Tree style" value={settings.treeStyle} onChange={updateSettings('treeStyle')}>
                  <MenuItem value="classic">Sage</MenuItem>
                  <MenuItem value="modern">Modern</MenuItem>
                  <MenuItem value="compact">Warm</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel id="tree-date-format-label">Date format</InputLabel>
                <Select labelId="tree-date-format-label" label="Date format" value={settings.dateFormat} onChange={updateSettings('dateFormat')}>
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel id="tree-font-size-label">Font size</InputLabel>
                <Select labelId="tree-font-size-label" label="Font size" value={settings.fontSize} onChange={updateSettings('fontSize')}>
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Button fullWidth onClick={handleSaveSettings} disabled={savingSettings || loading}
              startIcon={savingSettings ? <CircularProgress size={16} /> : <SaveIcon />}
              sx={{ mt: 0.6, minHeight: 38, color: 'var(--app-success)', fontWeight: 800 }}>
              Save tree settings
            </Button>
          </Box>
        </Box>

        <Box sx={{ gridArea: 'canvas', minWidth: 0 }}>
          <Box sx={{
            display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between',
            gap: { xs: 1.1, sm: 1 }, mb: 1, p: { xs: 1.3, sm: 1.5 }, borderRadius: 2.5,
            background: 'linear-gradient(145deg, rgba(22,27,34,0.98), rgba(17,24,32,0.96))',
            border: '1px solid var(--app-border-subtle)',
          }}>
            <Box>
              <Typography sx={{ color: 'var(--app-text-primary)', fontWeight: 900, fontSize: '0.92rem' }}>
                {settings.surname ? `${settings.surname} Family Tree` : 'Family Tree'}
              </Typography>
              <Typography sx={{ color: '#89949f', fontSize: '0.7rem', mt: 0.25 }}>
                {members.length} {members.length === 1 ? 'person' : 'people'} · connected by family relationships
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.3, sm: 1.1 }, mt: 0.8 }}>
                {[
                  { label: 'Parents', color: relationshipColors.parent },
                  { label: 'Spouses', color: relationshipColors.spouse },
                  { label: 'Siblings', color: relationshipColors.sibling },
                ].map(({ label, color }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 3, borderRadius: 2, bgcolor: color }} />
                  <Typography sx={{ color: '#9aa4af', fontSize: '0.66rem' }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap',
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
            }}>
              <TextField size="small" placeholder="Find a person" value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{ startAdornment: <SearchIcon sx={{ color: 'var(--app-text-secondary)', mr: 0.7, fontSize: 19 }} /> }}
                sx={{ width: { xs: '100%', sm: 180 }, order: { xs: -1, sm: 0 }, mb: { xs: 0.4, sm: 0 } }} />
              <IconButton aria-label="Zoom out" disabled={zoom <= 0.6}
                onClick={() => setZoom((value) => Math.max(0.6, Math.round((value - 0.1) * 10) / 10))}
                sx={{ color: 'var(--app-text-soft)', width: 42, height: 42 }}><ZoomOutIcon /></IconButton>
              <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.68rem', minWidth: 34, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <IconButton aria-label="Zoom in" disabled={zoom >= 1.6}
                onClick={() => setZoom((value) => Math.min(1.6, Math.round((value + 0.1) * 10) / 10))}
                sx={{ color: 'var(--app-text-soft)', width: 42, height: 42 }}><ZoomInIcon /></IconButton>
              <IconButton aria-label="Reset tree view" onClick={() => {
                setZoom(1);
                treeViewportRef.current?.scrollTo({ top: 0, left: 0 });
              }} sx={{ color: 'var(--app-text-soft)', width: 42, height: 42 }}><CenterFocusStrongIcon /></IconButton>
              <IconButton aria-label="Export family tree as SVG" disabled={!members.length} onClick={exportTree}
                sx={{ color: 'var(--app-success)', width: 42, height: 42 }}><DownloadIcon /></IconButton>
            </Box>
          </Box>
          <Box ref={treeViewportRef} sx={{
            height: { xs: 'min(54vh, 520px)', sm: 'min(64vh, 680px)', lg: 'min(76vh, 780px)' },
            minHeight: { xs: 320, sm: 420 },
            overflow: 'auto', borderRadius: 3, border: '1px solid var(--app-border-hover)',
            bgcolor: 'var(--app-background)',
            backgroundImage: 'radial-gradient(rgba(76,201,240,0.14) 0.7px, transparent 0.7px)',
            backgroundSize: '18px 18px', overscrollBehavior: 'contain', touchAction: 'pan-x pan-y',
            boxShadow: 'inset 0 1px 18px rgba(0,0,0,0.2)',
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: 'var(--app-success)' }} /></Box>
            ) : members.length === 0 ? (
              <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100%', p: 3, textAlign: 'center' }}>
                <Box>
                  <AccountTreeIcon sx={{ fontSize: 44, color: 'var(--app-success)', mb: 1 }} />
                  <Typography sx={{ color: 'var(--app-text-primary)', fontWeight: 900 }}>Your tree is ready to grow</Typography>
                  <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.78rem', mt: 0.5 }}>Add a relative to start building your family tree.</Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{
                position: 'relative', width: `max(100%, ${Math.round(treeWidth * zoom)}px)`,
                height: `${treeHeight * zoom}px`,
              }}>
                <Box sx={{
                  position: 'absolute', left: `max(0px, calc((100% - ${Math.round(treeWidth * zoom)}px) / 2))`,
                  top: 0, width: treeWidth, height: treeHeight,
                  transform: `scale(${zoom})`, transformOrigin: 'top left',
                }}>
                  <svg aria-hidden="true" width={treeWidth} height={treeHeight}
                    style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
                    {getRelationshipConnections(members, memberPositions, TREE_CARD_WIDTH, TREE_CARD_HEIGHT)
                      .map(({ type, key, d }) => (
                        <motion.path key={key}
                          initial={reduceMotion ? false : { pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: reduceMotion ? 0 : 0.55, ease: 'easeOut' }}
                          d={d}
                          fill="none"
                          stroke={relationshipColors[type]}
                          strokeOpacity={type === 'parent' ? 0.72 : 0.95}
                          strokeWidth={type === 'parent' ? 2 : 3}
                          strokeLinecap="round" />
                      ))}
                  </svg>
                  {rows.map((row, rowIndex) => (
                    <React.Fragment key={row.generation}>
                      <Typography sx={{
                        position: 'absolute', left: 10, top: TREE_PADDING + rowIndex * TREE_ROW_HEIGHT + 8,
                        color: 'var(--app-text-secondary)', fontSize: '0.62rem', fontWeight: 900, letterSpacing: '0.1em',
                      }}>
                        {row.generation === 0 ? 'ANCESTORS' : `GENERATION ${row.generation + 1}`}
                      </Typography>
                      {row.members.map((member, memberIndex) => {
                        const position = memberPositions.get(member.id);
                        const entranceDelay = Math.min(rowIndex * 0.12 + memberIndex * 0.045, 0.5);
                        return (
                          <motion.div key={member.id}
                            layout={reduceMotion ? false : 'position'}
                            initial={reduceMotion ? false : { opacity: 0, y: 16, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={reduceMotion ? { duration: 0 } : {
                              opacity: { duration: 0.28, delay: entranceDelay },
                              y: { duration: 0.38, delay: entranceDelay, ease: 'easeOut' },
                              scale: { duration: 0.38, delay: entranceDelay, ease: 'easeOut' },
                              layout: { type: 'spring', stiffness: 280, damping: 30 },
                            }}
                            style={{
                            position: 'absolute', left: position.x - TREE_CARD_WIDTH / 2, top: position.top,
                            outline: member.id === matchingMemberId ? `3px solid ${treeTheme.accent}` : 'none',
                            outlineOffset: 3, borderRadius: 2,
                          }}>
                            <MemberCard userId={userId} member={member} members={members} settings={settings}
                              onEdit={(selected) => { setDialogMember(selected); setDialogOpen(true); }}
                              onDelete={handleDeleteMember} />
                          </motion.div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <MemberDialog open={dialogOpen} member={dialogMember} members={members}
        defaultSurname={settings.surname} userId={userId}
        onClose={() => setDialogOpen(false)} onSave={handleSaveMember} />
    </Box>
  );
}
